import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { ITaskV2, ITaskV2Log } from '@fi-app/task-v2/fi-task-v2';
import { FiInfoIconMode } from '@fi-shared/info-icon/info-icon.component';
import { IDateByItem } from '@fi-app/shared/models/models';
import {
    ITaskActionBuilderContext,
    ITaskActionExecutedData,
} from '../../fi-task-v2-report-widget-details/widget-details-content/widget-details-table/task-actions.builder.service';
import { TaskActionsBuilderService } from '../../fi-task-v2-report-widget-details/widget-details-content/widget-details-table/task-actions.builder.service';
import { IItemsListAction2 } from '@fi-app/shared/fi-items-list/fi-items-list';
import { ITask2ReportItem } from '../../fi-task-v2-report';
import { BehaviorSubject, Subject } from 'rxjs';
import { FiWrapperTableMatMenuCellComponent } from '@fi-app/shared/fi-wrapper-table/cells/fi-wrapper-table-mat-menu-cell/fi-wrapper-table-mat-menu-cell.component';
import { isDefined } from '@fi-shared/utils';
import { FiTaskV2ReportHelperService } from '../../services/fi-task-v2-report-helper.service';
import { FiTaskOverviewTableColumns, FiTaskV2ReportAssigneesCellType } from '../../fi-task-v2-report-enums';
import { FiTaskStatusType } from '@fi-app/task-v2/fi-task-v2-enums';

@Component({
    selector: 'fi-task-overview-table-wrapper',
    templateUrl: './task-overview-table-wrapper.component.html',
    styleUrls: ['./task-overview-table-wrapper.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-task-overview-table-wrapper' },
    standalone: false,
})
export class FiTaskOverviewTableWrapperComponent implements OnInit, OnChanges {
    @Input({ required: true }) task: ITaskV2;
    @Input({ required: true }) latestTaskLogs: ITaskV2Log[] = [];
    @Input({ required: true }) lastReminder: IDateByItem;
    @Input({ required: true }) taskReport: ITask2ReportItem;
    @Input({ required: true }) actionExecuted$: Subject<ITaskActionExecutedData>;
    @Output() visibleColumnsChanged = new EventEmitter<number>();

    @Input({ required: true }) completedAfterDeadline: boolean;

    protected isActionsHidden$ = new BehaviorSubject<boolean>(false);
    @ViewChild(FiWrapperTableMatMenuCellComponent) set matMenuCell(
        value: FiWrapperTableMatMenuCellComponent<ITask2ReportItem>,
    ) {
        if (!isDefined(value)) {
            return;
        }
        this.isActionsHidden$.next(value.matIconCheckboxMenuComponent.hidden);
    }

    protected actions: ReadonlyArray<Readonly<IItemsListAction2<ITask2ReportItem>>>;
    protected readonly FiInfoIconMode = FiInfoIconMode;
    protected readonly actionsChanged$ = new Subject<void>();
    protected readonly FiTaskOverviewTableColumns = FiTaskOverviewTableColumns;

    private readonly cdr = inject(ChangeDetectorRef);
    private readonly taskActionsBuilderService = inject(TaskActionsBuilderService);
    private readonly fiTaskV2ReportHelperService = inject(FiTaskV2ReportHelperService);
    private readonly elementRef = inject(ElementRef);

    protected visibleColumns: FiTaskOverviewTableColumns[] = [
        ...this.fiTaskV2ReportHelperService.TaskReportOverviewDefaultVisibleColumns,
    ];

    public ngOnInit(): void {
        this.setTableColumnsData(true);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        const taskReportChange = changes.taskReport;
        const isTaskReportChanged = isDefined(taskReportChange) && !taskReportChange.firstChange;
        const taskChange = changes.task;
        const isTaskChanged = isDefined(taskChange) && !taskChange.firstChange;

        if (isTaskReportChanged || isTaskChanged) {
            this.setTableColumnsData();
        }
    }

    private setTableColumnsData(skipActionChangeEmitter?: boolean): void {
        this.actions = this.initializeActions();
        if (!skipActionChangeEmitter) {
            this.actionsChanged$.next();
        }
        this.visibleColumns = this.fiTaskV2ReportHelperService.getTaskReportOverviewTableVisibleColumns(
            this.task,
            this.latestTaskLogs,
            this.lastReminder,
            this.isTaskOverdue(),
        );
        this.setColumnMaxWidth();
    }

    private setColumnMaxWidth(): void {
        this.fiTaskV2ReportHelperService.setTaskOverviewTableColumnMaxWidth(
            this.elementRef.nativeElement,
            this.visibleColumns.length,
        );
        this.visibleColumnsChanged.emit(this.visibleColumns.length);
    }

    protected isColumnVisible(column: FiTaskOverviewTableColumns): boolean {
        return this.visibleColumns.includes(column);
    }

    private initializeActions(): ReadonlyArray<Readonly<IItemsListAction2<ITask2ReportItem>>> {
        const actionContext: ITaskActionBuilderContext = {
            cdr: this.cdr,
            actionExecuted$: this.actionExecuted$,
        };

        return this.taskActionsBuilderService.createTaskActions(actionContext);
    }

    protected isTaskInProgress(): boolean {
        return this.fiTaskV2ReportHelperService.isTaskInProgress(this.task);
    }

    protected isTaskCompleted(): boolean {
        return this.fiTaskV2ReportHelperService.isTaskCompleted(this.task);
    }

    protected isTaskOverdue(): boolean {
        return (
            this.task.IsDeadlinePassed &&
            (this.completedAfterDeadline ||
                [FiTaskStatusType.NotStarted, FiTaskStatusType.InProcess].includes(this.task.Status))
        );
    }

    protected getOverdueText(): string {
        return this.fiTaskV2ReportHelperService.getTaskReportOverviewOverdueText(this.task);
    }

    protected getCompletedOnData(): IDateByItem {
        return this.fiTaskV2ReportHelperService.getLatestLogCompletedOnData(this.latestTaskLogs);
    }

    protected getStartedOnData(): IDateByItem {
        return this.fiTaskV2ReportHelperService.getLatestLogStartedOnData(this.latestTaskLogs);
    }

    protected getArchivedOnData(): IDateByItem {
        return this.fiTaskV2ReportHelperService.getLatestLogArchivedOnData(this.latestTaskLogs);
    }

    protected getReopenedOnData(): IDateByItem {
        return this.fiTaskV2ReportHelperService.getLatestLogReopenedOnData(this.latestTaskLogs);
    }

    protected getCreatedOnData(): IDateByItem {
        return this.fiTaskV2ReportHelperService.convertCreatorToDateByItem(this.task);
    }

    protected readonly FiTaskV2ReportAssigneesCellType = FiTaskV2ReportAssigneesCellType;
}
